import * as ngCore from '@angular/core'
import type * as ents from '@attain-apps/entities'
import * as graphql from '@attain-apps/fe/web/main/lib/graphql'
import * as rxjs from 'rxjs'

import * as common from '../../common'

import * as gql from './gql'
import type * as models from './models'

const NON_NULL_AGENT_ID_TRANSFORMER = graphql.transformers.IN.nonNull(
  common.gql.enums.agentId.TRANSFORMER,
)

/**
 * GraphQL-backed API for the agent configuration feature.
 */
@ngCore.Injectable({ providedIn: 'root' })
export class Api {
  /**
   * Loads the agent default and (when conversationId is provided) the
   * per-conversation override in a single backend round-trip.
   */
  public load(
    context: ents.core.admin.Context.ForZone,
    request: models.LoadRequest,
  ): rxjs.Observable<models.LoadResponse> {
    const agentDefault$ = gql.queries.getAgentConfiguration.executor({
      agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(request.agentId),
      conversationId: undefined,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })

    const conversationOverride$ = request.conversationId
      ? gql.queries.getAgentConfiguration.executor({
          agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(request.agentId),
          conversationId: request.conversationId,
          tenantId: context.tenantId,
          zoneId: context.zoneId,
        })
      : rxjs.of(undefined)

    const knowledgeBaseFiles$ = gql.queries.getAgentKnowledgeBaseFiles.executor(
      {
        agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(request.agentId),
        tenantId: context.tenantId,
        zoneId: context.zoneId,
      },
    )

    return rxjs
      .forkJoin([agentDefault$, conversationOverride$, knowledgeBaseFiles$])
      .pipe(
        rxjs.map(
          ([agentDefault, conversationOverride, knowledgeBaseFiles]) => ({
            agentDefault,
            conversationOverride,
            knowledgeBaseFiles,
          }),
        ),
      )
  }

  /** Persists a configuration for the requested scope. */
  public save(
    context: ents.core.admin.Context.ForZone,
    { key, agentId, context: payload }: models.SaveRequest,
  ): rxjs.Observable<models.SaveResponse> {
    return gql.mutations.saveAgentConfiguration
      .executor({
        configuration: {
          agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(agentId),
          context: {
            dmsResources: JSON.stringify(payload.dmsResources),
            instructions: payload.instructions,
            knowledgeBaseFiles: payload.knowledgeBaseFiles?.slice(),
          },
          conversationId: key.conversationId,
        },
        tenantId: context.tenantId,
        zoneId: context.zoneId,
      })
      .pipe(
        rxjs.map(result => {
          if (!result) {
            throw new Error('Save returned no configuration')
          }
          return result
        }),
      )
  }

  /** Deletes a per-conversation override. */
  public deleteOverride(
    context: ents.core.admin.Context.ForZone,
    { agentId, conversationId }: models.DeleteOverrideRequest,
  ): rxjs.Observable<void> {
    return gql.mutations.deleteAgentConfiguration.executor({
      agentId: NON_NULL_AGENT_ID_TRANSFORMER.getGqlFrom(agentId),
      conversationId,
      tenantId: context.tenantId,
      zoneId: context.zoneId,
    })
  }
}
